/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { dataLayer } from '@weatherbug/common';
import { HurricaneOutlookSummaryRegion } from '@weatherbug/common/dist/data-layer/types/APIResponses/Hurricane/Outlook/hurricaneOutlookSummaryResponses';

import { reduxConnect } from '../../../../common';
import { translate } from '../../../../common/translation/translate';
import { NotificationBellIcon } from '../../../common/icons/notificationBellIcon';
import { HurricaneOutlookSummaryIconColours } from '../../constants';
import { smallMediaQuery } from '../../../../common/utilities/sizeHelpers';

import { IStdParameters, IStoreState } from '../../../common/interfaces/types';
import { GetHurricaneOutlookSummaryActionType } from '../../types';

const { getHurricaneOutlookSummary } = dataLayer.actions;
const isCached = false;
const verbose = false;

interface HurricaneOutlookMapNotificationProps {
    showNotificationOverlay: boolean;
    stdParameters: IStdParameters;
    latitude: number;
    longitude: number;
    hurricaneOutlookBaseUrl: string;
    authId: string;
    secret: string;
    hurricaneOutlookSummaryRegions: HurricaneOutlookSummaryRegion[];
    getHurricaneOutlookSummary({
        includeForecast7d,
        latitude,
        longitude,
        forceRefreshOfCachedData,
        baseUrl,
        authId,
        secret,
        stdApiParameters,
        verboseResponseKeys 
    }: GetHurricaneOutlookSummaryActionType): void;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { hurricaneOutlookSummary, appState } = storeState || {};

    const { data: hurricaneOutlookSummaryData } = hurricaneOutlookSummary || {};
    const { user, hurricaneOutlookBaseUrl, stdParameters, authId, secret } = appState?.appState || {};
    const { Latitude: latitude, Longitude: longitude } = user?.ActiveLocation?.Locality || {};

    return {
        showNotificationOverlay: typeof hurricaneOutlookSummaryData?.o?.dc === 'number' && hurricaneOutlookSummaryData?.o?.dc > 0,
        latitude,
        longitude,
        authId,
        secret,
        stdParameters,
        hurricaneOutlookBaseUrl
    };
};

const mapDispatchToProps = {
    getHurricaneOutlookSummary
};

export const HurricaneOutlookMapNotification = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    stdParameters, 
    latitude, 
    longitude,
    authId,
    secret,
    getHurricaneOutlookSummary, 
    hurricaneOutlookBaseUrl, 
    showNotificationOverlay }: HurricaneOutlookMapNotificationProps) => {

    useEffect(() => {
        if (Number.isFinite(latitude) && Number.isFinite(longitude) && authId && secret && hurricaneOutlookBaseUrl) {
            getHurricaneOutlookSummary(
                {
                    includeForecast7d: true,
                    latitude, 
                    longitude, 
                    forceRefreshOfCachedData: isCached,
                    baseUrl: hurricaneOutlookBaseUrl,
                    authId, 
                    secret,
                    stdApiParameters: stdParameters,
                    verboseResponseKeys: verbose
                });
        }
    }, [latitude, longitude, authId, secret, hurricaneOutlookBaseUrl, stdParameters]);

    if (!showNotificationOverlay) {
        return null;
    }
    return (
        <Wrapper>
            <Container>
                <IconContainer>
                    <NotificationBellIcon backgroundFillColor={HurricaneOutlookSummaryIconColours.activeStateYellow} />
                </IconContainer>
                <NotificationText>{translate('checkOutlookDetailsForMoreInfo')}</NotificationText>
            </Container>
        </Wrapper>
    );
});

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    position: absolute;
    z-index: 997;
    top: 25px;
    left: 16px;

    @media ${smallMediaQuery} {
        justify-content: center;
        left: 0px;
        width: 100%;
        padding: 8px;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 4px 8px;
    gap: 8px;
    width: 317px;
    height: 36px;
    background: #000000;
    opacity: 0.6;
    border-radius: 8px;

    @media ${smallMediaQuery} {
        width: 100%;
        margin: 0px 8px 0px 8px;
    }
`;

const IconContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 24px;
`;

const NotificationText = styled.span`
    display: flex;
    align-items: center;
    width: calc(100% - 33px);
    height: 18px;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    color: #FFFFFF;

    @media ${smallMediaQuery} {
        font-size: 12px;
        line-height: 16px;
    }
`;